import {useQuery, useMutation} from '@apollo/client'
import {Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {
  GET_ORGANIZATION_EMAIL_SETTINGS,
  GET_RESOLVED_EMAIL_SETTINGS,
} from '../../../gql/queries/emailSettingsQuery'
import {SEND_TEST_EMAIL, UPDATE_ORGANIZATION_EMAIL_SETTINGS} from '../../../gql/mutations/emailSettingsMutations'
import {EmailSetupForm, IEmailSetupValues} from '../../shared/EmailSetupForm'

interface Props {
  organizationId: string
}

export const OrganizationEmailSetupTab: React.FC<Props> = ({organizationId}) => {
  const {data, loading, refetch} = useQuery(GET_ORGANIZATION_EMAIL_SETTINGS, {
    variables: {organizationId},
    fetchPolicy: 'network-only',
  })
  const {data: resolvedData, refetch: refetchResolved} = useQuery(GET_RESOLVED_EMAIL_SETTINGS, {
    variables: {organizationId},
    fetchPolicy: 'network-only',
  })
  const [updateOrganizationEmailSettings, {loading: saving}] = useMutation(UPDATE_ORGANIZATION_EMAIL_SETTINGS)
  const [sendTestEmail, {loading: sendingTestEmail}] = useMutation(SEND_TEST_EMAIL)

  const settings = data?.organizationEmailSettings
  const resolved = resolvedData?.resolvedEmailSettings

  const handleSendTestEmail = async (toEmail: string) => {
    try {
      const res = await sendTestEmail({variables: {toEmail, organizationId}})
      const result = res.data?.sendTestEmail
      if (!result?.success) {
        toast.error(result?.errors?.map((e: any) => e.message).join('\n') || 'Error sending test email')
        return
      }
      toast.success(`Test email sent to ${toEmail}`)
    } catch (err: any) {
      toast.error(err.message || 'Error sending test email')
    }
  }

  const handleSave = async (input: IEmailSetupValues) => {
    const {publicRequireIdentity, ...rest} = input
    try {
      const res = await updateOrganizationEmailSettings({variables: {organizationId, input: rest}})
      const result = res.data?.updateOrganizationEmailSettings
      if (!result?.success) {
        toast.error(result?.errors?.map((e: any) => e.message).join('\n') || 'Error saving Email Setup')
        return
      }
      toast.success('Email Setup saved')
      refetch()
      refetchResolved()
    } catch (err: any) {
      toast.error(err.message || 'Error saving Email Setup')
    }
  }

  if (loading) {
    return (
      <div className='text-center py-6'>
        <Spinner animation='border' size='sm' />
      </div>
    )
  }

  return (
    <div>
      <p className='text-muted mb-4' style={{fontSize: '0.85rem'}}>
        SMTP transport for this organization's emails. Fields without override inherit the
        system default configuration (Policytools Settings → Email Setup).
      </p>
      <EmailSetupForm
        values={settings}
        resolved={resolved}
        showInheritance
        saving={saving}
        onSave={handleSave}
        onSendTestEmail={handleSendTestEmail}
        sendingTestEmail={sendingTestEmail}
      />
    </div>
  )
}
